import styled from 'styled-components';
import * as React from 'react';
import { useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import { Signal, useComputed, useSignal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import {
  HistoryCheckpoint,
  useAiMetadataApi,
} from '@/features/ai-agents/shared/use-ai-metadata-api';
import { getQueryParam } from '@/utils/browser';
import { groupBy } from '@/utils/arrays';
import { Spinner } from '@/Spinner';
import { capitalize } from '@/utils/types';

export interface CheckpointPopoverProps {
  currentSessionId: Signal<string>;
  firstSessionId: Signal<string>;
  lastRunId?: Signal<string>;
  onRestoreSession?: (checkpoint: HistoryCheckpoint) => Promise<void>;
}

export function CheckpointPopover({
  currentSessionId,
  firstSessionId,
  onRestoreSession,
  lastRunId,
}: CheckpointPopoverProps) {
  useSignals();
  const { getSessionCheckpoints } = useAiMetadataApi(
    getQueryParam(`local-ai`) === `1`,
  );

  const [firstLoading, setFirstLoading] = useState(true);
  const restoreLoading = useSignal(false);
  const checkpoints = useSignal<HistoryCheckpoint[]>(null);

  const checkpointsGroups = useComputed(() => {
    if (checkpoints) {
      return groupBy(checkpoints.value, (i) => i.dateString);
    }
    return null;
  });

  const selectedCheckpoint = useComputed(() =>
    getSelectedCheckpoint(
      checkpoints.value,
      lastRunId?.value,
      currentSessionId.value,
    ),
  );

  useAsyncEffect(async () => {
    setFirstLoading(true);
    try {
      const resp = await getSessionCheckpoints(firstSessionId.value);
      if (resp) {
        checkpoints.value = resp
          ?.map((i) => {
            i.date = new Date(i.timestamp);
            i.dateString = formatDateWithOrdinal(i.date);
            return i;
          })
          .sort((a, b) => b.date.getTime() - a.date.getTime());
      }
    } finally {
      setFirstLoading(false);
    }
  }, []);

  const onRestore = async (checkpoint: HistoryCheckpoint) => {
    restoreLoading.value = true;
    await onRestoreSession?.(checkpoint);
    restoreLoading.value = false;
  };

  return (
    <Wrapper className={firstLoading ? `loading` : ``}>
      {firstLoading && <Spinner mt={0} size={30} border={2} />}
      {!firstLoading &&
        (!checkpointsGroups.value ||
          Object.keys(checkpoints.value).length === 0) && (
          <GroupName
            style={{
              textAlign: `center`,
              width: `100%`,
              height: `100%`,
              display: `flex`,
              justifyContent: `center`,
              alignItems: `center`,
              margin: `auto`,
            }}
          >
            No checkpoints available
          </GroupName>
        )}
      {!firstLoading && checkpointsGroups.value && (
        <List>
          {Object.entries(checkpointsGroups.value)?.map(
            ([dateString, items]) => (
              <Group key={dateString}>
                <GroupName>{dateString}</GroupName>
                <GroupCheckpoints>
                  {items?.map((item) => (
                    <CheckpointLine
                      key={item.new_session_id}
                      className={
                        selectedCheckpoint.value.run_id === item.run_id
                          ? `selected`
                          : ``
                      }
                      onClick={() => !item.isSelected && onRestore?.(item)}
                    >
                      <CheckpointName>
                        {capitalize(item.input, true)}
                      </CheckpointName>
                      <CheckpointTime>
                        {item.date.toLocaleTimeString([], {
                          hour: `2-digit`,
                          minute: `2-digit`,
                        })}
                      </CheckpointTime>
                    </CheckpointLine>
                  ))}
                </GroupCheckpoints>
              </Group>
            ),
          )}
        </List>
      )}
      {restoreLoading.value && (
        <RestoreBackdrop>
          <Spinner mt={0} size={30} border={2} />
        </RestoreBackdrop>
      )}
    </Wrapper>
  );
}

function getSelectedCheckpoint(
  checkpoints: HistoryCheckpoint[],
  lastRunId: string,
  currentSessionId: string,
): HistoryCheckpoint | null {
  if (!checkpoints || checkpoints.length === 0) {
    return null;
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const checkpoint of checkpoints) {
    if (checkpoint.run_id === lastRunId) {
      return checkpoint;
    }
    if (checkpoint.new_session_id === currentSessionId) {
      return checkpoint;
    }
    if (checkpoint.checkpoint_session_id === currentSessionId) {
      return checkpoint;
    }
  }
  return checkpoints?.[0] || null;
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  background: white;

  display: flex;
  flex-direction: column;
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid #dedede;

  min-height: 10rem;
  min-width: 41rem;

  font-family: 'Eesti', serif;
  box-shadow: 0px 1px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  &&.loading {
    align-items: center;
    justify-content: center;
  }
`;

const RestoreBackdrop = styled.div`
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(231, 231, 231, 0.6);
  backdrop-filter: blur(4px);
  z-index: 100;
  pointer-events: none;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  max-height: 50rem;
  overflow-y: scroll;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const GroupName = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
`;

const GroupCheckpoints = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-left: 0.3rem;
`;

const CheckpointLine = styled.div`
  font-size: 1.25rem;
  color: black;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem;
  border-radius: 0.4rem;
  gap: 1rem;

  cursor: pointer;

  &&:hover {
    background: #f5f5f5;
  }

  &&.selected {
    color: rgb(81, 85, 150);
    background: rgba(137, 192, 221, 0.151) !important;
    && > * {
      font-weight: bold !important;
    }
  }
`;

const CheckpointName = styled.span`
  font-weight: 500;
  max-width: 25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CheckpointTime = styled.span`
  font-size: 1.1rem;
`;

function formatDateWithOrdinal(date: Date): string {
  const day = date.getDate();
  const ordinal =
    // eslint-disable-next-line no-nested-ternary
    day % 10 === 1 && day !== 11
      ? `st`
      : // eslint-disable-next-line no-nested-ternary
      day % 10 === 2 && day !== 12
      ? `nd`
      : day % 10 === 3 && day !== 13
      ? `rd`
      : `th`;
  const month = date.toLocaleString(`en-US`, { month: `long` });
  const year = date.getFullYear();
  return `${month} ${day}${ordinal}, ${year}`;
}
